import { useState, useCallback, useEffect, useRef } from 'react'
import { usePendingPointerBlock } from '@papan/files/ui/pending-pointer-gate'
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  Link2,
  Settings,
  Play,
} from 'lucide-react'
import { AIOverrideBadge } from './AIOverrideBadge'
import { isMiniappFolder, enforcedAIOverride } from '@papan/types'
import { requestMiniappView } from '@papan/collab/client'
import { useQueryClient } from '@tanstack/react-query'
import { FileIcon, fileIconProps, miniappIconProps } from '@papan/core-ui/ui'
import { filesApi, onListedRowRefused, type FileListItem } from '@papan/files/client'
import { isSessionEndedError } from '@papan/api-client'
import { fileKeys, IdentityChangedError } from '@papan/files/client/queries'
import { getActiveDragFolderIds, setActiveDragFolderIds, isCircularMove, useFolderAncestors } from '@papan/files/client/dnd'
import { peekPapanDragFolderIds } from '@papan/core-ui/ui/papanDrag'
import { useRealtime } from '@papan/realtime/client'
// Root folder detected by deterministic ID: papan_${tenantId}

interface FileNode {
  id: string
  name: string
  isFolder: boolean
  size: number
  /** DB row-kind column — drives the icon's Network/link overlay so a demoted
   *  or hand-made `*.shared` file (fileType='file') shows the plain icon, not
   *  the name-derived wifi badge. */
  fileType?: string | null
  /** D17: a `.chain` still needing approval — drawn grey by `fileIconProps`. */
  /** Code-file approval (#1452): Approve on this row (the content no person set); `contentHash` is what it names. */
  codeNeedsApproval?: boolean
  codeCanApprove?: boolean
  /** A folder with a code file needing approval below it — a warning on the folder row. */
  codeApprovalBelow?: boolean
  contentHash?: string | null
  chainNeedsApproval?: boolean
  chainCanApprove?: boolean
  chainBroken?: boolean
  /** The owner shared this pointer WITHOUT onward-sharing rights, so a share link on it
   *  is refused (`shareLinkOffered`). A review found that gate was a no-op for every row
   *  in here, because the flag never made it out of the listing. */
  reshareBlocked?: boolean
  /** The pointer is dead — a mint on it is a hard 403 (`shareLinkOffered`). */
  shareBroken?: boolean
  /** Folder row's metadata_json — carries the miniappIcon spec. */
  metadataJson?: string | null
  children?: FileNode[]
  isLoaded?: boolean
  isLoading?: boolean
}


// Filter utilities shared with WorkspaceExplorer and FilePickerDialog
import { pathMatchesFilter, nodeMatchesFilter, sortFileItems } from './fileFilter'
import { pointerFactsFromListing, type ContextMenuRow } from './listingRow'
import { CodeApprovalBadge } from './codeApproval'
import { markListCacheStale, loadedFolderIds, readTreeFolder, useLatestFolderLoads, type FolderLoad } from './treeStale'

function TreeItem({
  item,
  parentId,
  parentName,
  depth,
  selectedIds,
  activeFileId,
  autoExpandIds,
  renamingId,
  draggedIds,
  dropTargetId,
  forbiddenDropId,
  scope,
  aiOverrideMap,
  inheritedOverride,
  userPermMap,
  inheritedUserPerm,
  onSelect,
  onFileOpen,
  onLoadChildren,
  onItemContextMenu,
  onRenameCommit,
  onRenameCancel,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  filterText,
  filterAnchored,
  filterPathPrefix,
}: {
  item: FileNode
  parentId: string | null
  parentName: string | null
  depth: number
  selectedIds: ReadonlySet<string>
  activeFileId: string | null
  autoExpandIds: Set<string> | null
  renamingId: string | null
  draggedIds: ReadonlySet<string>
  dropTargetId: string | null
  forbiddenDropId: string | null
  scope: string
  aiOverrideMap?: Map<string, 'hidden' | 'r'>
  inheritedOverride?: 'hidden' | 'r'
  userPermMap?: Map<string, string>
  inheritedUserPerm?: string
  onSelect: (id: string, name: string, isFolder: boolean, e?: React.MouseEvent, parentId?: string | null, parentName?: string | null) => void
  onFileOpen: (fileId: string) => void
  onLoadChildren: (folderId: string) => void
  onItemContextMenu?: (item: ContextMenuRow, e: React.MouseEvent) => void
  onRenameCommit: (id: string, newName: string, currentName?: string) => void
  onRenameCancel: () => void
  onDragStart: (e: React.DragEvent, item: FileNode) => void
  onDragOver: (e: React.DragEvent, item: FileNode) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, item: FileNode) => void
  onDragEnd: () => void
  filterText?: string
  filterAnchored?: boolean
  filterPathPrefix?: string
}) {
  const [expanded, setExpanded] = useState(() => !!filterText && item.isFolder)
  const blockPendingPointer = usePendingPointerBlock()
  const isRenaming = renamingId === item.id
  const [renameValue, setRenameValue] = useState(item.name)
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Auto-expand folders when filter text changes (but let user collapse them)
  const prevFilterRef = useRef(filterText)
  useEffect(() => {
    if (filterText && filterText !== prevFilterRef.current && item.isFolder) {
      setExpanded(true)
    }
    prevFilterRef.current = filterText
  }, [filterText, item.isFolder])

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus()
      const dotIdx = item.name.lastIndexOf('.')
      if (dotIdx > 0 && !item.isFolder) {
        renameInputRef.current.setSelectionRange(0, dotIdx)
      } else {
        renameInputRef.current.select()
      }
      setRenameValue(item.name)
    }
  }, [isRenaming, item.name, item.isFolder])

  // Auto-re-fetch children when folder is expanded but data was reset
  useEffect(() => {
    if (expanded && item.isFolder && !item.isLoaded && !item.isLoading) {
      onLoadChildren(item.id)
    }
  }, [expanded, item.isFolder, item.isLoaded, item.isLoading, item.id, onLoadChildren])

  // Auto-expand when parent requests reveal
  useEffect(() => {
    if (autoExpandIds?.has(item.id) && item.isFolder && !expanded) {
      setExpanded(true)
    }
  }, [autoExpandIds, item.id, item.isFolder, expanded])

  // Auto-load children when filter is active but subfolder children haven't been fetched
  useEffect(() => {
    if (filterText && item.isFolder && !item.isLoaded && !item.isLoading) {
      onLoadChildren(item.id)
    }
  }, [filterText, item.isFolder, item.isLoaded, item.isLoading, item.id, onLoadChildren])

  const handleClick = (e: React.MouseEvent) => {
    if (isRenaming) return
    onSelect?.(item.id, item.name, item.isFolder, e, parentId, parentName)
    // Only open file / toggle folder on plain click
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
      // D17 / I114 — a chain that Needs approval doesn't open or expand.
      if (blockPendingPointer(item)) return
      if (item.isFolder) {
        const wasExpanded = expanded
        setExpanded(!wasExpanded)
        if (!wasExpanded && !item.isLoaded) {
          onLoadChildren(item.id)
        }
      } else {
        onFileOpen(item.id)
      }
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    if (onItemContextMenu) {
      e.preventDefault()
      e.stopPropagation()
      onItemContextMenu({ ...item, parentId, parentName }, e)
    }
  }

  const commitRename = () => {
    const trimmed = renameValue.trim()
    if (trimmed && trimmed !== item.name) {
      onRenameCommit(item.id, trimmed, item.name)
    } else {
      onRenameCancel()
    }
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRename()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onRenameCancel()
    }
  }

  const isDragging = draggedIds.has(item.id)
  const isDropTarget = dropTargetId === item.id
  const isForbidden = forbiddenDropId === item.id

  // Effective override: own override wins if more restrictive, otherwise inherit from parent
  const ownOverride = aiOverrideMap?.get(item.id)
  const effectiveOverride = ownOverride === 'hidden' || inheritedOverride === 'hidden'
    ? 'hidden' as const
    : ownOverride || inheritedOverride

  // User permission: own entry wins, otherwise inherit from parent
  const ownUserPerm = userPermMap?.get(item.id)
  const effectiveUserPerm = ownUserPerm || inheritedUserPerm
  const userReadOnly = effectiveUserPerm ? !['admin', 'readwrite', 'owner'].includes(effectiveUserPerm) : false

  // Filter: hide items that don't match (after all hooks to comply with Rules of Hooks)
  if (filterText && !nodeMatchesFilter(item, filterText, filterPathPrefix || '/', !!filterAnchored)) return null

  return (
    <>
      <div
        data-file-id={item.id}
        data-file-name={item.name}
        data-file-is-folder={item.isFolder ? '1' : '0'}
        data-file-parent-id=""
        data-file-scope={scope}
        draggable={!isRenaming}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onDragStart={(e) => onDragStart(e, item)}
        onDragOver={(e) => item.isFolder && onDragOver(e, item)}
        onDragLeave={onDragLeave}
        onDrop={(e) => item.isFolder && onDrop(e, item)}
        onDragEnd={onDragEnd}
        className={`group flex items-center gap-1 w-full text-left py-1 px-1 rounded text-sm cursor-pointer ${
          isForbidden
            ? 'bg-red-600/20 border border-red-500/50'
            : isDropTarget
            ? 'bg-green-600/30 border border-green-500'
            : isDragging
              ? 'opacity-50 themed-text-secondary'
              : selectedIds.has(item.id)
                ? 'bg-brand-500/20 themed-text'
                : activeFileId === item.id
                  ? 'themed-bg-tertiary themed-text'
                  : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
        }`}
        style={{ paddingLeft: `${depth * 12 + 4}px`, ...(enforcedAIOverride(effectiveOverride) === 'hidden' && !(ownOverride === 'hidden' && inheritedOverride !== 'hidden') ? { opacity: 0.3 } : {}) }}
      >
        {item.isFolder ? (
          item.isLoading ? (
            <Loader2 size={14} className="animate-spin shrink-0" />
          ) : expanded ? (
            <ChevronDown size={14} className="shrink-0" />
          ) : (
            <ChevronRight size={14} className="shrink-0" />
          )
        ) : (
          <span className="w-[5px] shrink-0" />
        )}
        <FileIcon {...fileIconProps({ ...item, name: isRenaming ? renameValue : item.name })} {...miniappIconProps(item)} miniappIconName={isRenaming ? renameValue : item.name} />
        {isRenaming ? (
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={commitRename}
            className="flex-1 min-w-0 bg-transparent border themed-border rounded px-1 py-0 text-sm themed-text outline-none focus:border-brand-500"
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <>
            <span className="truncate">{item.name}</span>
            <CodeApprovalBadge row={item} />
            <AIOverrideBadge override={effectiveOverride} userReadOnly={userReadOnly} />
            {item.isFolder && isMiniappFolder(item.name) && (
              <span className="ml-auto flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); requestMiniappView(item.id, 'run'); onFileOpen(item.id) }}
                  className="p-0.5 themed-text-muted hover:themed-text"
                  data-tooltip="Preview app"
                  aria-label="Preview app"
                >
                  <Play size={13} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); requestMiniappView(item.id, 'settings'); onFileOpen(item.id) }}
                  className="p-0.5 themed-text-muted hover:themed-text"
                  data-tooltip="Mini app settings"
                  aria-label="Mini app settings"
                >
                  <Settings size={13} />
                </button>
              </span>
            )}
          </>
        )}
      </div>
      {item.isFolder && expanded && item.children && (
        <div
          data-children-of={item.id}
          onDragOver={(e) => {
            // Accept drops in the expanded area as drops onto the parent folder
            e.preventDefault()
            onDragOver(e, item)
          }}
          onDragLeave={onDragLeave}
          onDrop={(e) => {
            e.preventDefault()
            onDrop(e, item)
          }}
        >
          {item.children.map(child => (
            <TreeItem
              key={child.id}
              item={child}
              parentId={item.id}
              parentName={item.name}
              depth={depth + 1}
              selectedIds={selectedIds}
              activeFileId={activeFileId}
              autoExpandIds={autoExpandIds}
              renamingId={renamingId}
              draggedIds={draggedIds}
              dropTargetId={dropTargetId}
              forbiddenDropId={forbiddenDropId}
              scope={scope}
              aiOverrideMap={aiOverrideMap}
              inheritedOverride={effectiveOverride}
              userPermMap={userPermMap}
              inheritedUserPerm={effectiveUserPerm}
              onSelect={onSelect}
              onFileOpen={onFileOpen}
              onLoadChildren={onLoadChildren}
              onItemContextMenu={onItemContextMenu}
              onRenameCommit={onRenameCommit}
              onRenameCancel={onRenameCancel}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              filterText={filterText}
              filterAnchored={filterAnchored}
              filterPathPrefix={(filterPathPrefix || '/') + item.name.toLowerCase() + '/'}
            />
          ))}
        </div>
      )}
    </>
  )
}

interface LinkedFolderTreeProps {
  linkId: string
  targetFolderId: string
  displayName: string
  selectedIds: ReadonlySet<string>
  activeFileId: string | null
  autoExpandIds: Set<string> | null
  renamingId: string | null
  renamingScope: string | null
  draggedIds: ReadonlySet<string>
  dropTargetId: string | null
  forbiddenDropId: string | null
  aiOverrideMap?: Map<string, 'hidden' | 'r'>
  userPermMap?: Map<string, string>
  onFileOpen: (fileId: string) => void
  onSelect: (id: string, name: string, isFolder: boolean, e?: React.MouseEvent, parentId?: string | null, parentName?: string | null) => void
  onContextMenu: (linkId: string, targetFolderId: string, folderName: string, e: React.MouseEvent) => void
  onItemContextMenu?: (item: ContextMenuRow, e: React.MouseEvent) => void
  onRenameCommit: (id: string, newName: string, currentName?: string) => void
  onRenameCancel: () => void
  onDragStart: (e: React.DragEvent, item: { id: string; name: string; isFolder: boolean }) => void
  onDragOver: (e: React.DragEvent, item: { id: string; isFolder: boolean }) => void
  onForbiddenDragOver: (targetId: string) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, item: { id: string; isFolder: boolean }) => void
  onDragEnd: () => void
  filterText?: string
  /** Bump to force reload children */
  refreshTrigger?: number
  /** Bumped on a permission change or a code-file approval: re-read every loaded folder, not only the root. */
  deepRefreshTrigger?: number
}

export function LinkedFolderTree({ linkId, targetFolderId, displayName, selectedIds, activeFileId, autoExpandIds, renamingId, renamingScope, draggedIds, dropTargetId, forbiddenDropId, aiOverrideMap, userPermMap, onFileOpen, onSelect, onContextMenu, onItemContextMenu, onRenameCommit, onRenameCancel, onDragStart, onDragOver, onForbiddenDragOver, onDragLeave, onDrop, onDragEnd, filterText, refreshTrigger, deepRefreshTrigger }: LinkedFolderTreeProps) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(() => !!filterText)
  const [children, setChildren] = useState<FileNode[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  // #939: `displayName` is the link's name in the workspace config as loaded when the
  // project opened — stale once the folder is renamed elsewhere. Start from the cached file
  // info when there is one (the realtime rename refresh writes it), so a remount — collapsing
  // and re-opening "Linked from Drives" — doesn't bring the old name back.
  const [folderName, setFolderName] = useState(() =>
    queryClient.getQueryData<{ name?: string }>(fileKeys.detail(targetFolderId))?.name ?? displayName)
  // Only show rename if this tree's linkId matches the rename scope
  const effectiveRenamingId = renamingScope === linkId ? renamingId : null
  const isRootRenaming = effectiveRenamingId === targetFolderId
  const [rootRenameValue, setRootRenameValue] = useState(folderName)
  const rootRenameRef = useRef<HTMLInputElement>(null)
  // The name the box opened with — if the person leaves it unchanged, nothing was renamed,
  // even when the folder was renamed elsewhere meanwhile (#939).
  const rootRenameStartRef = useRef(folderName)

  // Focus and select when root rename starts — only then: a rename elsewhere that lands
  // while the box is open must not replace what the person is typing (#939).
  useEffect(() => {
    if (isRootRenaming && rootRenameRef.current) {
      rootRenameRef.current.focus()
      rootRenameRef.current.select()
      setRootRenameValue(folderName)
      rootRenameStartRef.current = folderName
    }
  }, [isRootRenaming]) // eslint-disable-line react-hooks/exhaustive-deps

  const commitRootRename = () => {
    const trimmed = rootRenameValue.trim()
    if (trimmed && trimmed !== rootRenameStartRef.current && trimmed !== folderName) {
      // The name shown now — it can have changed since the rename started (#939); undo and
      // the no-op check key on it.
      onRenameCommit(targetFolderId, trimmed, folderName)
    } else {
      onRenameCancel()
    }
  }

  const handleRootRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRootRename()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onRenameCancel()
    }
  }

  // Detect root by deterministic ID pattern: papan_${tenantId}
  const isRoot = targetFolderId.startsWith('papan_')

  // #939: the ONE way this tree reads its folder's name from the server — on mount and on a
  // realtime rename. Straight from the server, NOT through the query cache: a cached fetch
  // shares a request still in flight, so a read started after a rename would get the answer
  // of one started before it (the old name) — and invalidating doesn't end that request
  // (nothing observes this key). Only the newest read may set the label (and the cache); an
  // older one that answers late is dropped.
  const nameReadRef = useRef(0)
  // A tree that closes (e.g. "Linked from Drives" collapsed) supersedes its own reads, so a
  // late answer from before a rename can't write the old name into the shared cache.
  const aliveRef = useRef(true)
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; nameReadRef.current++ } }, [])
  const readFolderName = useCallback(async () => {
    if (!aliveRef.current) return // a closed tree starts no read (its answer could only write a stale name)
    const read = ++nameReadRef.current
    try {
      const info = await filesApi.getFileInfo(targetFolderId)
      if (read !== nameReadRef.current) return
      queryClient.setQueryData(fileKeys.detail(targetFolderId), info)
      setFolderName(info.name)
    } catch { /* unreadable (deleted, no access) — keep the name shown */ }
  }, [targetFolderId, queryClient])

  useEffect(() => {
    if (isRoot) {
      setFolderName('papan (root)')
    } else {
      // Read the name on every mount, not only when the config had none (#939): the config's
      // name can be stale, and so can a cached one (a rename missed while the socket was down).
      void readFolderName()
    }
  }, [displayName, isRoot, readFolderName])

  // Pre-fetch ancestor chain for centralized circular move detection
  const ancestorIds = useFolderAncestors(targetFolderId)

  // Resolved permission for the root folder (from listChildren parentPermission)
  const [resolvedRootPerm, setResolvedRootPerm] = useState<string | null>(null)
  // Folder loads, coordinated as in WorkspaceExplorer (#1058): only a folder's
  // latest load settles it, a load asked before a resync asks again, and a
  // folder's first load after a resync reads past the cache.
  const folderLoader = useCallback<FolderLoad>(async (folderId, settle, { fresh, reask }) => {
    // A resync's re-ask of a folder no longer in this tree asks nothing. (A
    // re-ask runs through the latest committed render's loader —
    // `useLatestFolderLoads` — so `targetFolderId` is the tree's current root.)
    if (reask && folderId !== targetFolderId && !hasNodeInTree(childrenRef.current, folderId)) { await settle(() => {}, false); return }
    try {
      const apiId = folderId
      // TODO: limit: 500 is a workaround — tree should load all children.
      // Need virtual scrolling or server-side search for folders with 500+ files.
      // Route through TanStack Query so the WorkspaceExplorer + sibling
      // LinkedFolderTrees that all react to the same realtime event share
      // a single in-flight fetch.
      const opts = { sortBy: 'name' as const, sortDir: 'asc' as const, limit: 500 }
      // Through `fetchAndCacheList`: one in-flight read per folder shared with the other trees, and
      // never an answer that predates an access change (#1260) or is for another identity (#1210).
      const res = await readTreeFolder(queryClient, apiId, opts, fresh ? 0 : 1000)
      const nodes: FileNode[] = sortFileItems(res.files.map((f: FileListItem) => ({
        id: f.id,
        name: f.name,
        isFolder: f.isFolder,
        size: f.size,
        // The same one place the explorer's two mappings use — see `listingRow.ts`.
        ...pointerFactsFromListing(f),
        metadataJson: f.metadataJson,
        children: f.isFolder ? [] : undefined,
        isLoaded: false,
        isLoading: false,
      })))

      await settle(() => {
        if (folderId === targetFolderId) {
          // Capture parent permission for the root folder
          if (res.parentPermission) setResolvedRootPerm(res.parentPermission)
          // No `mount` descriptor at all = an ordinary folder of your own, nothing to
          // refuse. It is also what the listing's documented fail-safe returns for a
          // folder that IS a mount, so this is fail-open — the same way the drive reads
          // it, and the mint is gated server-side regardless.
          // Merge: keep old children mounted, mark stale for auto-re-fetch
          setChildren(prev => mergeChildren(nodes, prev))
        } else {
          // Update nested children (updateNodeChildren also merges)
          setChildren(prev => updateNodeChildren(prev, folderId, nodes))
        }
      })
    } catch (err) {
      // The identity changed again during the re-ask (#1210): not a failure, nothing to log.
      // So is a session ended under it (#1316 × #1210 — an identity switch ends the session first).
      if (err instanceof IdentityChangedError || isSessionEndedError(err)) { await settle(() => {}, false); return }
      console.error('[LinkedFolderTree] Failed to load:', folderId, err)
      await settle(() => {}, false)
    }
  }, [targetFolderId, queryClient])
  const { load: loadChildren, resync: resyncFolderLoads } = useLatestFolderLoads(folderLoader)

  // Auto-expand root when parent requests reveal
  useEffect(() => {
    if (autoExpandIds?.has(targetFolderId) && !expanded) {
      if (!loaded) {
        setLoading(true)
        loadChildren(targetFolderId).then(() => {
          setLoading(false)
          setLoaded(true)
        })
      }
      setExpanded(true)
    }
  }, [autoExpandIds, targetFolderId, expanded, loaded, loadChildren])

  // Auto-expand root when filter text changes (but let user collapse it)
  const prevRootFilterRef = useRef(filterText)
  useEffect(() => {
    if (filterText && filterText !== prevRootFilterRef.current) {
      setExpanded(true)
    }
    prevRootFilterRef.current = filterText
  }, [filterText])

  // Auto-load children when filter is active but children haven't been fetched yet
  useEffect(() => {
    if (filterText && !loaded && !loading) {
      setLoading(true)
      loadChildren(targetFolderId).then(() => {
        setLoading(false)
        setLoaded(true)
      })
    }
  }, [filterText, loaded, loading, loadChildren, targetFolderId])

  const handleToggle = async (e?: React.MouseEvent) => {
    onSelect?.(targetFolderId, folderName, true, e)
    if (!expanded && !loaded) {
      setLoading(true)
      await loadChildren(targetFolderId)
      setLoading(false)
      setLoaded(true)
    }
    setExpanded(!expanded)
  }

  // Reload when refreshTrigger bumps
  useEffect(() => {
    if (refreshTrigger && loaded) {
      loadChildren(targetFolderId)
    }
  }, [refreshTrigger]) // eslint-disable-line react-hooks/exhaustive-deps
  // A permission change or a code-file approval can change ANY loaded row, not only the root's: re-read the root and
  // every loaded folder — only on those signals (the plain trigger above fires per upload and per broad event)
  useEffect(() => {
    if (deepRefreshTrigger && loaded) {
      // each list marked stale first, so a read cached in the last second (before the change committed) is not reused
      for (const id of [targetFolderId, ...loadedFolderIds(childrenRef.current)]) {
        void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', id], refetchType: 'none' })
        loadChildren(id)
      }
    }
  }, [deepRefreshTrigger]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadNestedChildren = useCallback(async (folderId: string) => {
    // Mark as loading
    setChildren(prev => updateNodeField(prev, folderId, { isLoading: true }))
    await loadChildren(folderId)
    setChildren(prev => updateNodeField(prev, folderId, { isLoading: false, isLoaded: true }))
  }, [loadChildren])

  // Auto-refresh on realtime file events
  const { onFileEvent } = useRealtime()
  const refreshFolderName = useCallback(() => {
    if (isRoot) return
    void readFolderName()
  }, [isRoot, readFolderName])

  // Auto-refresh on realtime file events.
  //
  // We collect the parentId / oldParentId of each event during a 500ms
  // debounce. If neither parent is inside our subtree (root or any expanded
  // descendant) we skip — there's nothing to update. This keeps a move in a
  // sibling linked tree from triggering a refresh of every other tree.
  // Events that don't carry parentId fall back to a wholesale root reload.
  const childrenRef = useRef<FileNode[]>(children)
  childrenRef.current = children
  const lftRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lftPendingParentsRef = useRef<Set<string | null>>(new Set())
  const lftPendingBroadRef = useRef(false)
  // #939: an event about the linked folder ITSELF (renamed or moved) carries its own
  // parent — a level above this tree — so the parent check below never matches it. Track
  // it separately and re-read the folder's name, or the root keeps its old label until
  // something inside it happens to change.
  const lftPendingSelfRef = useRef(false)
  // A pending batch dies with the tree: nothing may fire after it closes.
  useEffect(() => () => { if (lftRefreshTimerRef.current) clearTimeout(lftRefreshTimerRef.current) }, [])
  useEffect(() => {
    return onFileEvent((event) => {
      const ev = event as typeof event & { parentId?: string | null; oldParentId?: string | null }
      if (ev.fileId === targetFolderId) {
        lftPendingSelfRef.current = true
        // The event carries the folder's new name: show it now rather than after a server
        // round trip, and supersede any read still out (it was asked before this rename). The
        // debounced read below confirms it.
        if (ev.name && !isRoot) {
          nameReadRef.current++
          setFolderName(ev.name)
        }
      }
      const hasParent = ev.parentId !== undefined
      const hasOldParent = ev.oldParentId !== undefined
      if (!hasParent && !hasOldParent) {
        lftPendingBroadRef.current = true
      } else {
        if (hasParent) lftPendingParentsRef.current.add(ev.parentId ?? null)
        if (hasOldParent) lftPendingParentsRef.current.add(ev.oldParentId ?? null)
      }

      if (lftRefreshTimerRef.current) clearTimeout(lftRefreshTimerRef.current)
      lftRefreshTimerRef.current = setTimeout(() => {
        lftRefreshTimerRef.current = null
        const parents = lftPendingParentsRef.current
        const broad = lftPendingBroadRef.current
        const self = lftPendingSelfRef.current
        lftPendingParentsRef.current = new Set()
        lftPendingBroadRef.current = false
        lftPendingSelfRef.current = false

        if (broad) {
          refreshFolderName()
          if (loaded) loadChildren(targetFolderId)
          return
        }

        // Only refresh parents that are actually inside our tree.
        // Invalidate the shared cache key first so a sibling subscriber
        // (WorkspaceExplorer or another LinkedFolderTree containing the
        // same parent) firing ms later hits a fresh fetch rather than
        // returning a value that predates this event.
        let nameRefreshed = false
        if (self) { refreshFolderName(); nameRefreshed = true }
        for (const pid of parents) {
          if (pid === null) continue // root files don't belong to any linked tree
          if (pid === targetFolderId) {
            if (!nameRefreshed) { refreshFolderName(); nameRefreshed = true }
            queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', pid] })
            if (loaded) loadChildren(targetFolderId)
          } else if (hasNodeInTree(childrenRef.current, pid)) {
            queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', pid] })
            loadChildren(pid)
          }
          // else: parent is in a different tree entirely — ignore
        }
      }, 500)
    })
  }, [onFileEvent, loaded, loadChildren, targetFolderId, refreshFolderName, queryClient, isRoot])

  // #1264 — an action on a row of this tree was refused (it may be gone, its delete never announced):
  // re-read the folder that holds it here. A row elsewhere is not ours.
  useEffect(() => onListedRowRefused((fileId) => {
    // the tree's own root (a refused rename / move of the linked folder): re-read its name once — a
    // name read never reports a refusal, so this can't loop
    if (fileId === targetFolderId) { refreshFolderName(); return }
    const parent = parentInTree(childrenRef.current, fileId, targetFolderId)
    if (parent === undefined) return
    void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', parent] })
    if (parent === targetFolderId) { if (loaded) loadChildren(targetFolderId) } else loadChildren(parent)
  }), [targetFolderId, loaded, loadChildren, queryClient, refreshFolderName])

  // #1058: a resync — events were missed while the socket was down. No event
  // will name what changed, so re-read the folder's name and its listing. The
  // listing's merge (`mergeChildren`) marks every row it keeps stale, so each
  // expanded subfolder reloads in turn, down the tree. The list cache is marked
  // stale first (no refetch), as in WorkspaceExplorer: those reloads read
  // through it with a 1 s `staleTime`. A read still in flight at the
  // reconnect asks again inside its own fetch, so one joining it gets the
  // post-reconnect rows (the resync boundary, #1210).
  // `FileListCacheSync` and the explorer mark the same prefix on the same
  // signal — redundant scans of a rare event, kept so no tree depends on which
  // handler runs first (code review).
  const { onResync } = useRealtime()
  useEffect(() => {
    return onResync((kind) => {
      if (kind !== 'file') return
      resyncFolderLoads()
      markListCacheStale(queryClient)
      refreshFolderName()
      if (loaded) void loadChildren(targetFolderId)
    })
  }, [onResync, loaded, loadChildren, targetFolderId, refreshFolderName, queryClient, resyncFolderLoads])

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onContextMenu(linkId, targetFolderId, folderName, e)
  }

  // Wrap parent's onDragStart to include ALL loaded descendant folder IDs
  // in the module-level activeDragFolderIds set. This lets OTHER LinkedFolderTrees
  // detect that their root is a descendant of the dragged folder.
  const handleDragStartWithDescendants = useCallback((e: React.DragEvent, item: { id: string; name: string; isFolder: boolean }) => {
    onDragStart(e, item) // parent sets activeDragFolderIds to [item.id]
    // Override with expanded set that includes all loaded descendants
    if (item.isFolder) {
      const allIds: string[] = [item.id]
      if (item.id === targetFolderId) {
        allIds.push(...collectFolderIds(children))
      } else {
        // Dragging a subfolder within this tree — find its subtree
        const findAndCollect = (nodes: FileNode[]): void => {
          for (const n of nodes) {
            if (n.id === item.id && n.children) {
              allIds.push(...collectFolderIds(n.children))
              return
            }
            if (n.children) findAndCollect(n.children)
          }
        }
        findAndCollect(children)
      }
      setActiveDragFolderIds(allIds)
    }
  }, [children, targetFolderId, onDragStart])

  // Wrap parent's onDragOver to add descendant check using this tree's local data.
  // The parent's handleItemDragOver only checks workspaceFiles, not linked tree children.
  // When forbidden, we call onForbiddenDragOver (NOT onDragOver) so the parent sets
  // forbiddenDropId without overriding it with a green drop target.
  const handleDragOverWithCheck = useCallback((e: React.DragEvent, item: { id: string; isFolder: boolean }) => {
    if (!item.isFolder) return

    // Dragged folder ids from the drag store (cross-tab safe) plus module-level
    // state (has same-tab descendant ids)
    const fromStore = peekPapanDragFolderIds(e.dataTransfer)
    const fromModule = getActiveDragFolderIds()
    const allDragIds = new Set([...fromStore, ...fromModule])
    // Also include internal workspace draggedId
    for (const id of draggedIds) allDragIds.add(id)

    if (allDragIds.size > 0) {
      // Centralized check: is any dragged folder the target or an ancestor of it?
      if (isCircularMove(allDragIds, item.id, ancestorIds)) {
        e.preventDefault()
        e.stopPropagation()
        e.dataTransfer.dropEffect = 'none'
        onForbiddenDragOver(item.id)
        return
      }
      // Also check within this tree's loaded data (for same-tree descendant detection)
      for (const fId of allDragIds) {
        if (findInLinkedTree(children, fId, item.id)) {
          e.preventDefault()
          e.stopPropagation()
          e.dataTransfer.dropEffect = 'none'
          onForbiddenDragOver(item.id)
          return
        }
      }
    }

    onDragOver(e, item)
  }, [draggedIds, children, ancestorIds, onDragOver, onForbiddenDragOver])

  // ── Render-time circular drop detection (same-tab fallback) ─────────
  // Uses module-level state + ancestorIds to catch cases where the event
  // handler's forbidden state didn't persist through React batching.
  const isDropCircular = (() => {
    if (!dropTargetId) return false
    const inThisTree = dropTargetId === targetFolderId || hasNodeInTree(children, dropTargetId)
    if (!inThisTree) return false

    const allDragIds = new Set(getActiveDragFolderIds())
    for (const id of draggedIds) allDragIds.add(id)
    if (allDragIds.size === 0) return false

    if (isCircularMove(allDragIds, dropTargetId, ancestorIds)) return true
    for (const fId of allDragIds) {
      if (findInLinkedTree(children, fId, dropTargetId)) return true
    }
    return false
  })()

  // Override visual state: show red instead of green for circular targets
  const effectiveDropTargetId = isDropCircular ? null : dropTargetId
  const effectiveForbiddenDropId = isDropCircular ? dropTargetId : forbiddenDropId

  const isRootDragging = draggedIds.has(targetFolderId)
  const isRootDropTarget = effectiveDropTargetId === targetFolderId
  const isRootForbidden = effectiveForbiddenDropId === targetFolderId
  const rootItem = { id: targetFolderId, name: folderName, isFolder: true as const }

  // Root folder's effective AI override (cascades to all children)
  const rootOverride = aiOverrideMap?.get(targetFolderId)

  // Root folder's user permission (cascades to children that don't have their own)
  // Falls back to parentPermission from API when no explicit folder_permissions entry exists
  const rootUserPerm = userPermMap?.get(targetFolderId) || resolvedRootPerm || undefined
  const rootUserReadOnly = rootUserPerm ? !['admin', 'readwrite', 'owner'].includes(rootUserPerm) : false

  // Leading "/" means "from papan root" — only the root linked folder matches.
  // For non-root linked folders, "/" prefix means no match.
  const slashPrefixed = filterText?.startsWith('/')
  const filterAnchored = !!slashPrefixed

  // Build the path prefix for this linked folder's children.
  // For "/" prefix on root: use "/" (root is consumed, children match from /)
  // For non-"/" filters: use "folderName/" so full drive path is matched
  const childPathPrefix = slashPrefixed
    ? '/'
    : folderName.toLowerCase() + '/'

  // Filter: hide entire linked folder if nothing matches.
  if (filterText && loaded) {
    if (slashPrefixed && !isRoot) return null // "/" means papan root only
    const rootPath = folderName.toLowerCase() + '/'
    const rootMatches = pathMatchesFilter(rootPath, filterText, filterAnchored)
    const childrenMatch = children.some(child =>
      nodeMatchesFilter(child, filterText, childPathPrefix, filterAnchored)
    )
    if (!rootMatches && !childrenMatch) return null
  }

  return (
    <div>
      <div
        data-file-id={targetFolderId}
        data-file-name={folderName}
        data-file-is-folder="1"
        data-file-parent-id=""
        data-file-scope={linkId}
        draggable={!isRootRenaming}
        onClick={isRootRenaming ? undefined : (e: React.MouseEvent) => handleToggle(e)}
        onContextMenu={handleContextMenu}
        onDragStart={(e) => handleDragStartWithDescendants(e, rootItem)}
        onDragOver={(e) => handleDragOverWithCheck(e, rootItem)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, rootItem)}
        onDragEnd={onDragEnd}
        className={`group flex items-center gap-1 w-full text-left py-1 px-1 rounded text-sm cursor-pointer ${
          isRootForbidden
            ? 'bg-red-600/20 border border-red-500/50'
            : isRootDropTarget
            ? 'bg-green-600/30 border border-green-500'
            : isRootDragging
              ? 'opacity-50 themed-text-secondary'
              : selectedIds.has(targetFolderId)
                ? 'bg-brand-500/20 themed-text'
                : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
        }`}
        style={{ paddingLeft: '4px' }}
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin shrink-0" />
        ) : expanded ? (
          <ChevronDown size={14} className="shrink-0" />
        ) : (
          <ChevronRight size={14} className="shrink-0" />
        )}
        <Link2 size={14} className="text-blue-400 shrink-0" />
        {isRootRenaming ? (
          <input
            ref={rootRenameRef}
            value={rootRenameValue}
            onChange={e => setRootRenameValue(e.target.value)}
            onKeyDown={handleRootRenameKeyDown}
            onBlur={commitRootRename}
            className="flex-1 min-w-0 bg-transparent border themed-border rounded px-1 py-0 text-sm themed-text outline-none focus:border-brand-500"
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <>
            <span className="truncate">{folderName}</span>
            <AIOverrideBadge override={rootOverride} userReadOnly={rootUserReadOnly} />
            {isMiniappFolder(folderName) && (
              <span className="ml-auto flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); requestMiniappView(targetFolderId, 'run'); onFileOpen(targetFolderId) }}
                  className="p-0.5 themed-text-muted hover:themed-text"
                  data-tooltip="Preview app"
                  aria-label="Preview app"
                >
                  <Play size={13} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); requestMiniappView(targetFolderId, 'settings'); onFileOpen(targetFolderId) }}
                  className="p-0.5 themed-text-muted hover:themed-text"
                  data-tooltip="Mini app settings"
                  aria-label="Mini app settings"
                >
                  <Settings size={13} />
                </button>
              </span>
            )}
          </>
        )}
      </div>

      {expanded && (
        <div
          data-children-of={targetFolderId}
          onDragOver={(e) => {
            e.preventDefault()
            handleDragOverWithCheck(e, rootItem)
          }}
          onDragLeave={onDragLeave}
          onDrop={(e) => {
            e.preventDefault()
            onDrop(e, rootItem)
          }}
        >
          {children.map(child => (
            <TreeItem
              key={child.id}
              item={child}
              parentId={targetFolderId}
              parentName={folderName}
              depth={1}
              selectedIds={selectedIds}
              activeFileId={activeFileId}
              autoExpandIds={autoExpandIds}
              renamingId={effectiveRenamingId}
              draggedIds={draggedIds}
              dropTargetId={effectiveDropTargetId}
              forbiddenDropId={effectiveForbiddenDropId}
              scope={linkId}
              aiOverrideMap={aiOverrideMap}
              inheritedOverride={rootOverride}
              userPermMap={userPermMap}
              inheritedUserPerm={rootUserPerm}
              onSelect={onSelect}
              onFileOpen={onFileOpen}
              onLoadChildren={handleLoadNestedChildren}
              onItemContextMenu={onItemContextMenu}
              onRenameCommit={onRenameCommit}
              onRenameCancel={onRenameCancel}
              onDragStart={handleDragStartWithDescendants}
              onDragOver={handleDragOverWithCheck}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              filterText={filterText}
              filterAnchored={filterAnchored}
              filterPathPrefix={childPathPrefix}
            />
          ))}
          {loaded && children.length === 0 && (
            <div className="pl-8 py-1 text-xs themed-text-muted">Empty folder</div>
          )}
        </div>
      )}
    </div>
  )
}

function updateNodeChildren(items: FileNode[], id: string, newChildren: FileNode[]): FileNode[] {
  return items.map(item => {
    if (item.id === id) {
      // Merge: preserve expanded grandchildren, mark stale for auto-re-fetch
      const merged = item.children?.length
        ? mergeChildren(newChildren, item.children)
        : newChildren
      return { ...item, children: merged, isLoaded: true }
    }
    if (item.children) {
      return { ...item, children: updateNodeChildren(item.children, id, newChildren) }
    }
    return item
  })
}

/**
 * Merge fresh items with previous: keep old children mounted (prevents unmount),
 * mark stale (isLoaded: false) so auto-re-fetch gets fresh data.
 */
function mergeChildren(newItems: FileNode[], prevItems: FileNode[]): FileNode[] {
  const prevMap = new Map<string, FileNode>()
  for (const item of prevItems) {
    prevMap.set(item.id, item)
  }
  return newItems.map(item => {
    const prev = prevMap.get(item.id)
    if (prev?.isFolder && prev.children?.length) {
      return { ...item, children: prev.children, isLoaded: false }
    }
    return item
  })
}

function updateNodeField(items: FileNode[], id: string, updates: Partial<FileNode>): FileNode[] {
  return items.map(item => {
    if (item.id === id) {
      return { ...item, ...updates }
    }
    if (item.children) {
      return { ...item, children: updateNodeField(item.children, id, updates) }
    }
    return item
  })
}

/** Check if targetId exists in the loaded subtree of ancestorId */
function findInLinkedTree(items: FileNode[], ancestorId: string, targetId: string): boolean {
  for (const item of items) {
    if (item.id === ancestorId && item.children) {
      return hasNodeInTree(item.children, targetId)
    }
    if (item.children && findInLinkedTree(item.children, ancestorId, targetId)) {
      return true
    }
  }
  return false
}

/** #1264 — the folder that holds `id` in this tree (`rootId` for a top-level row), or undefined. */
function parentInTree(items: FileNode[], id: string, rootId: string): string | undefined {
  for (const item of items) {
    if (item.id === id) return rootId
    if (item.children) {
      const p = parentInTree(item.children, id, item.id)
      if (p !== undefined) return p
    }
  }
  return undefined
}

function hasNodeInTree(items: FileNode[], id: string): boolean {
  for (const item of items) {
    if (item.id === id) return true
    if (item.children && hasNodeInTree(item.children, id)) return true
  }
  return false
}

/** Collect all folder IDs from a tree (for drag descendant tracking) */
function collectFolderIds(items: FileNode[]): string[] {
  const ids: string[] = []
  for (const item of items) {
    if (item.isFolder) {
      ids.push(item.id)
      if (item.children) ids.push(...collectFolderIds(item.children))
    }
  }
  return ids
}
